/**
 * VEEZE about page — the team swipe rail's dots.
 *
 * The rail itself is CSS (`scroll-snap-type: x mandatory`), so swiping works
 * without this file. All this adds is the pagination: it reveals the dots only
 * once the rail can actually scroll, keeps `aria-current` on the card in view,
 * and scrolls to a card when its dot is pressed.
 *
 * A `ResizeObserver` re-checks scrollability because the rail is only a rail
 * below 750px — above that the grid fits and the dots must disappear again.
 */
class VeezeTeamRail extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('[data-rail]');
    this.dots = this.querySelector('[data-dots]');
    if (!this.rail || !this.dots) return;

    this.buttons = Array.from(this.dots.querySelectorAll('button'));
    this.onScroll = this.onScroll.bind(this);
    this.sync = this.sync.bind(this);

    this.rail.addEventListener('scroll', this.onScroll, { passive: true });
    this.dots.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button) return;
      const card = this.cards()[this.buttons.indexOf(button)];
      if (card) this.rail.scrollTo({ left: card.offsetLeft - this.rail.offsetLeft, behavior: 'smooth' });
    });

    this.observer = new ResizeObserver(this.sync);
    this.observer.observe(this.rail);
    this.sync();
  }

  disconnectedCallback() {
    this.rail?.removeEventListener('scroll', this.onScroll);
    this.observer?.disconnect();
  }

  cards() {
    return Array.from(this.rail.children);
  }

  /* A one-pixel tolerance: sub-pixel layout can leave a rail "scrollable" by
     a fraction of a pixel when it in fact fits. */
  sync() {
    const scrollable = this.rail.scrollWidth - this.rail.clientWidth > 1;
    this.dots.dataset.scrollable = String(scrollable);
    if (scrollable) this.onScroll();
  }

  /* The cards snap to `start`, so the active one is whichever card's leading edge
     is nearest the scroll position — not whichever is nearest the rail's centre.
     Measuring from the centre marks card 2 active at rest whenever more than one
     card is visible, which is exactly what the design shows on a 375 screen. */
  onScroll() {
    const start = this.rail.scrollLeft;
    let active = 0;
    let best = Infinity;

    this.cards().forEach((card, index) => {
      const edge = card.offsetLeft - this.rail.offsetLeft;
      const distance = Math.abs(edge - start);
      if (distance < best) {
        best = distance;
        active = index;
      }
    });

    this.buttons.forEach((button, index) => {
      if (index === active) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('veeze-team-rail')) {
  customElements.define('veeze-team-rail', VeezeTeamRail);
}
